import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { acceptTerms, fetchCurrentTerms } from '@/lib/api/terms.service'
import type { CurrentTermsBundlePayload } from '@/types/legal-documents'
import {
  isConfirmationPhraseValid,
  isScrollComplete,
  resolveTermsAcceptanceRequired,
  TERMS_CONFIRMATION_PHRASE,
} from '@/lib/terms-acceptance'
import { ApiClientError } from '@/lib/api/client'

export function useTermsAcceptance() {
  const { guard, token, isLoading, isCollaborator, institution, collaborator, refreshMe } =
    useAuth()

  const acceptor = isCollaborator ? collaborator : institution

  const needsAcceptance = useMemo(() => {
    if (guard !== 'institution' || isLoading) return false
    return resolveTermsAcceptanceRequired(acceptor, { failSafeWhenMissing: true })
  }, [guard, isLoading, acceptor])

  const [terms, setTerms] = useState<CurrentTermsBundlePayload | null>(null)
  const [termsError, setTermsError] = useState<string | null>(null)
  const [isTermsLoading, setIsTermsLoading] = useState(false)

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
  const [hasAgreed, setHasAgreed] = useState(false)
  const [confirmationPhrase, setConfirmationPhrase] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const scrollRef = useRef<HTMLDivElement | null>(null)

  const loadTerms = useCallback(async () => {
    if (!token || !needsAcceptance) return
    setIsTermsLoading(true)
    setTermsError(null)
    try {
      const payload = await fetchCurrentTerms(token)
      setTerms(payload)
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : 'Não foi possível carregar os termos de uso.'
      setTermsError(message)
    } finally {
      setIsTermsLoading(false)
    }
  }, [needsAcceptance, token])

  useEffect(() => {
    if (!needsAcceptance) {
      setTerms(null)
      setHasScrolledToEnd(false)
      setHasAgreed(false)
      setConfirmationPhrase('')
      setSubmitError(null)
      return
    }
    void loadTerms()
  }, [needsAcceptance, loadTerms])

  useEffect(() => {
    if (!needsAcceptance) return
    setHasScrolledToEnd(false)
    setHasAgreed(false)
    setConfirmationPhrase('')
    setSubmitError(null)
  }, [terms?.bundle_version, needsAcceptance])

  useEffect(() => {
    if (!terms || !needsAcceptance) return
    const frame = window.requestAnimationFrame(() => {
      if (isScrollComplete(scrollRef.current)) {
        setHasScrolledToEnd(true)
      }
    })
    return () => window.cancelAnimationFrame(frame)
  }, [terms, needsAcceptance])

  const handleScroll = useCallback(() => {
    if (hasScrolledToEnd) return
    if (isScrollComplete(scrollRef.current)) {
      setHasScrolledToEnd(true)
    }
  }, [hasScrolledToEnd])

  const handleAgreeChange = useCallback((checked: boolean) => {
    if (!hasScrolledToEnd) return
    setHasAgreed(checked)
    if (!checked) {
      setConfirmationPhrase('')
      setSubmitError(null)
    }
  }, [hasScrolledToEnd])

  const confirmationPhraseHint =
    terms?.confirmation_phrase?.trim() || TERMS_CONFIRMATION_PHRASE

  const isPhraseValid = isConfirmationPhraseValid(
    confirmationPhrase,
    confirmationPhraseHint
  )
  const canSubmit = hasAgreed && isPhraseValid && !isSubmitting

  const handleSubmit = useCallback(async () => {
    if (!token || !canSubmit) return
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await acceptTerms(token, confirmationPhrase.trim())
      await refreshMe(token)
    } catch (error) {
      if (error instanceof ApiClientError && error.errors?.confirmation_phrase?.[0]) {
        setSubmitError(error.errors.confirmation_phrase[0])
      } else {
        setSubmitError(
          error instanceof ApiClientError
            ? error.message
            : 'Não foi possível registrar o aceite. Tente novamente.'
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [canSubmit, confirmationPhrase, refreshMe, token])

  return {
    needsAcceptance,
    terms,
    termsError,
    isTermsLoading,
    scrollRef,
    hasScrolledToEnd,
    hasAgreed,
    confirmationPhrase,
    setConfirmationPhrase,
    submitError,
    isSubmitting,
    canSubmit,
    confirmationPhraseHint,
    handleScroll,
    handleAgreeChange,
    handleSubmit,
    retryLoadTerms: loadTerms,
  }
}
